/**
 * Y-Site compatibility board — shared types.
 *
 * Named for the CAPABILITY, not the agent, which is why this sits in
 * components/ysite/ rather than in apps/compatibility beside the board: the
 * chat's remoteDom host renders a Y-site matrix for whichever agent's tool call
 * returned one, and the agent-representations preview renders it too. The
 * platform holds the READING of a Y-site matrix (this shape, its verdict tones,
 * its label parsing, CompatibilityCard); the compatibility agent's app holds
 * the editing of one. Same split as components/roster/ ↔ apps/roster.
 *
 * The board state mirrors the institution's catheter-compatibility matrix
 * (today: USB LL0034-V07). It is delivered to the app through the usual
 * session interface (the `compatibility` agent), NOT a bespoke client model.
 *
 * Drug identity in the matrix is the **label** (concentration-specific), not
 * the SNOMED id: the same SNOMED concept appears at multiple concentrations
 * (e.g. "Heparin Natrium 100 E/ml" and "…200 E/ml" both map to 372877000).
 * So `id === label` and `snomed` is carried separately for medication lookups.
 */

export type VerdictTone = 'ok' | 'warn' | 'bad' | 'muted'

/**
 * The five verdict statuses present in the live USB LL0034-V07 dataset.
 * (matrix-classes.md still names `compatible_with_flush`; the shipped data
 * uses `requires_line_flush` + an extra `compatible_with_all_carriers`. The
 * data is the source of truth — this union tracks it.)
 */
export type VerdictId =
  | 'compatible_same_lumen'
  | 'compatible_with_all_carriers'
  | 'requires_line_flush'
  | 'incompatible'
  | 'no_data'

/** Clinical-severity tone for each verdict — drives cell + verdict-card color. */
export const VERDICT_TONE: Record<VerdictId, VerdictTone> = {
  compatible_same_lumen: 'ok',
  compatible_with_all_carriers: 'ok',
  requires_line_flush: 'warn',
  incompatible: 'bad',
  no_data: 'muted',
}

/** Ordered list of verdicts for legends / roll-up summaries. */
export const VERDICT_ORDER: VerdictId[] = [
  'compatible_same_lumen',
  'compatible_with_all_carriers',
  'requires_line_flush',
  'incompatible',
  'no_data',
]

export interface CompatibilityDrug {
  /** Canonical key — the matrix label (unique per row). */
  id: string
  /** Display label, e.g. "Aggrastat (Tirofiban) 50 µg/ml". */
  label: string
  /** ATC code, when known. */
  atc?: string
  /** SNOMED concept id — NOT unique across concentrations. */
  snomed?: string
  /**
   * Sentinel rows in the source matrix ("(all carriers)", "(any other
   * drug)") that are not real selectable drugs but carry blanket verdicts.
   */
  sentinel?: boolean
}

export interface CompatibilityCell {
  verdict: VerdictId
  /** Verdict inferred rather than read verbatim from the source matrix. */
  derived?: boolean
  /** Source document is outside its review window (valid_until passed). */
  outdated?: boolean
}

export interface CompatibilityBoardState {
  /** Institution slug, e.g. "usb". */
  institution: string
  /** Source document id, e.g. "USB/LL0034-V07". */
  source_doc: string
  source_version?: string
  /** ISO date the source became valid. */
  valid_from: string
  /** ISO date the source review window ends. */
  valid_until: string
  /** All drugs (matrix rows/columns). */
  drugs: CompatibilityDrug[]
  /** Cells keyed by `pairKey(idA, idB)` — order-independent. */
  cells: Record<string, CompatibilityCell>
}

/**
 * Order-independent canonical key for a drug pair. The `|` separator never
 * appears in a drug label (labels are names + concentrations: letters, digits,
 * spaces, parens, µ, %, /, ., -), so it can't collide the way a space would
 * ("A B" + "C" vs "A" + "B C"). MUST match the key format the matrix source is
 * generated with (scripts/gen-matrix-fixture.mjs) or every lookup misses.
 */
export function pairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`
}

/** Look up the cell for a pair (null for self-pairs or missing entries). */
export function getCell(
  state: CompatibilityBoardState,
  a: string,
  b: string,
): CompatibilityCell | null {
  if (a === b) return null
  return state.cells[pairKey(a, b)] ?? null
}
